import { useEffect, useRef, useState } from "react";

/** ---------------------- Tipos da API ---------------------- */
export type SaidaItemAPI = {
  id: number;
  movimentacao_id: number;
  produto_id: number;
  quantidade: number;
  custo_unitario: string | null;
  produto: {
    id: number;
    codigo: string;
    nome: string;
    posicao: string | null;
  };
};

export type RetornoSolicitacaoAPI = {
  id: number | null;
  status: "solicitado" | "aprovado" | "negado" | null;
  quantidade: number | null;
  motivo: string | null;
  observacao: string | null;
  motivo_negativa: string | null;
  criado_em: string | null;
  analisado_em: string | null;
};

export type SaidaMovAPI = {
  movimentacao: {
    id: number;
    tipo?: "servico" | "venda" | "consumo" | "outro" | string;
    tipo_full?: string | null;
    responsavel_id: number | null;
    responsavel_nome: string | null; // C. Responsável
    departamento_origem: string | null;
    os_numero?: string | null;
    coletor_nome?: string | null; // Técnico
    criado_em: string;
    retorno_solicitacao?: RetornoSolicitacaoAPI | null;
  };
  itens: SaidaItemAPI[];
};

type RespAPI = {
  ok: boolean;
  meta: {
    filtros: {
      date_from: string | null;
      date_to: string | null;
      order: "ASC" | "DESC";
      limit: number;
      offset: number;
    };
    count: number;
  };
  data: SaidaMovAPI[];
};

/** ---------------------- Constantes ---------------------- */
const BASE = "https://api.eva-googlemarine.com/departamentos/suprimentos";
const URL = `${BASE}/saidas`;
const URL_AWAIT = `${BASE}/saidas/await`;

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

/** ---------------------- Utils ---------------------- */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return { data: iso, hora: "" };
  }

  return {
    data: d.toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
    }),
    hora: d.toLocaleTimeString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
}

function formatBRL(x: number): string {
  return x.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** ---------------------- Tabela ---------------------- */
/**
 * Ordem:
 * ["OS",
 *  "Código",
 *  "Nome do Produto",
 *  "Quantidade",
 *  "Custo Unitário",
 *  "Técnico",
 *  "C. Responsável",
 *  "Data",
 *  "Hora"]
 */
export type SaidaRowUI = [
  string, // OS
  string, // Código
  string, // Nome do Produto
  string, // Quantidade
  string, // Custo Unitário
  string, // Técnico
  string, // C. Responsável
  string, // Data
  string // Hora
];

export type RetornoStatusUI = "none" | "solicitado" | "aprovado" | "negado";

export type SaidaRowMeta = {
  movimentacaoId: number;
  produtoId: number;
  itemId: number;
  osNumero: string;
  tipoFull: string;
  quantidade: number;
  custoUnitario: number;
  tecnico: string;
  responsavelId: number | null;
  responsavelNome: string | null;

  retornoSolicitacaoId: number | null;
  retornoStatus: RetornoStatusUI;
  retornoQuantidade: number | null;
  retornoMotivo: string | null;
  retornoObservacao: string | null;
  retornoMotivoNegativa: string | null;
  retornoCriadoEm: string | null;
  retornoAnalisadoEm: string | null;
};

function normalizeRetornoStatus(
  status: RetornoSolicitacaoAPI["status"] | undefined
): RetornoStatusUI {
  if (status === "solicitado") return "solicitado";
  if (status === "aprovado") return "aprovado";
  if (status === "negado") return "negado";
  return "none";
}

/** Normaliza a resposta da API para linhas + meta */
function flattenToRows(movs: SaidaMovAPI[]): {
  rows: SaidaRowUI[];
  rowMeta: SaidaRowMeta[];
} {
  const rows: SaidaRowUI[] = [];
  const rowMeta: SaidaRowMeta[] = [];

  for (const m of movs) {
    const isServico = m.movimentacao.tipo_full === "standby-servico-saida";
    if (!isServico) continue;

    const { data, hora } = toBRT(m.movimentacao.criado_em);
    const os = m.movimentacao.os_numero || "-";
    const tecnico = m.movimentacao.coletor_nome || "-";
    const responsavel = m.movimentacao.responsavel_nome || "-";

    const retorno = m.movimentacao.retorno_solicitacao || null;
    const retornoStatus = normalizeRetornoStatus(retorno?.status);

    for (const it of m.itens) {
      const qtd = Number(it.quantidade || 0);
      const custo = Number(it.custo_unitario ?? "0");

      rows.push([
        os,
        it.produto.codigo,
        it.produto.nome,
        String(qtd),
        `R$ ${formatBRL(custo)}`,
        tecnico,
        responsavel,
        data,
        hora,
      ]);

      rowMeta.push({
        movimentacaoId: m.movimentacao.id,
        produtoId: it.produto.id,
        itemId: it.id,
        osNumero: os,
        tipoFull: m.movimentacao.tipo_full || "-",
        quantidade: qtd,
        custoUnitario: custo,
        tecnico,
        responsavelId: m.movimentacao.responsavel_id,
        responsavelNome: m.movimentacao.responsavel_nome,

        retornoSolicitacaoId: retorno?.id ?? null,
        retornoStatus,
        retornoQuantidade: retorno?.quantidade ?? null,
        retornoMotivo: retorno?.motivo ?? null,
        retornoObservacao: retorno?.observacao ?? null,
        retornoMotivoNegativa: retorno?.motivo_negativa ?? null,
        retornoCriadoEm: retorno?.criado_em ?? null,
        retornoAnalisadoEm: retorno?.analisado_em ?? null,
      });
    }
  }

  return { rows, rowMeta };
}

/** ---------------------- Fetchers ---------------------- */
export type SaidasQuery = Partial<{
  date_from: string;
  date_to: string;
  order: "asc" | "desc";
  limit: number;
  offset: number;
}>;

function buildQuery(q?: SaidasQuery): string {
  if (!q) return "";
  const p = new URLSearchParams();
  if (q.date_from) p.set("date_from", q.date_from);
  if (q.date_to) p.set("date_to", q.date_to);
  if (q.order) p.set("order", q.order);
  if (typeof q.limit === "number") p.set("limit", String(q.limit));
  if (typeof q.offset === "number") p.set("offset", String(q.offset));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export async function fetchSaidasOnce(query?: SaidasQuery): Promise<{
  rows: SaidaRowUI[];
  rowMeta: SaidaRowMeta[];
  meta: RespAPI["meta"] | null;
}> {
  const res = await fetch(`${URL}${buildQuery(query)}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });

  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const json = (await res.json()) as RespAPI;

  if (!json.ok) {
    return { rows: [], rowMeta: [], meta: json.meta ?? null };
  }

  const flat = flattenToRows(json.data);

  return {
    rows: flat.rows,
    rowMeta: flat.rowMeta,
    meta: json.meta ?? null,
  };
}

/** ---------------------- Await (long-poll) ---------------------- */
type AwaitResult = { changed: boolean; versao: number | null };

async function awaitChange(
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const url = `${URL_AWAIT}?since=${encodeURIComponent(
      since
    )}&timeout_ms=${AWAIT_TIMEOUT_MS}`;

    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });

    if (!res.ok) return null;

    const json = await res.json();
    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;
    const changed = Boolean(json?.changed);

    return { changed, versao };
  } catch {
    return null;
  }
}

/** ---------------------- Hook ---------------------- */
export function useSaidasSuprimentos(query?: SaidasQuery) {
  const [rows, setRows] = useState<SaidaRowUI[]>([]);
  const [rowMeta, setRowMeta] = useState<SaidaRowMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);

  const stop = useRef(false);
  const fetching = useRef(false);

  const versaoRef = useRef<number | null>(null);
  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitSupported = useRef<boolean | null>(null);
  const awaitInFlightRef = useRef(false);

  async function carregar() {
    fetching.current = true;
    try {
      const r = await fetchSaidasOnce(query);
      setRows(r.rows);
      setRowMeta(r.rowMeta);
      setMeta(r.meta);
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    versaoRef.current = null;
    awaitSupported.current = null;
    awaitCtrlRef.current?.abort();

    (async () => {
      try {
        await carregar();
        setLoading(false);

        const init = await awaitChange(-1);
        if (init && typeof init.versao === "number") {
          versaoRef.current = init.versao;
          awaitSupported.current = true;
        } else {
          awaitSupported.current = false;
        }

        while (!stop.current) {
          while (fetching.current && !stop.current) await sleep(25);
          if (stop.current) break;

          const cur = versaoRef.current;

          if (awaitSupported.current !== false && cur != null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const ac = new AbortController();
            awaitCtrlRef.current = ac;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;
            try {
              resp = await awaitChange(cur, ac.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError") {
                await sleep(jitter(ERROR_BACKOFF_MS));
              }
            } finally {
              if (awaitCtrlRef.current === ac) {
                awaitCtrlRef.current = null;
              }
              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              if (awaitSupported.current === null) {
                awaitSupported.current = false;
              }
              await sleep(POLL_FALLBACK_MS);
              continue;
            } else if (awaitSupported.current === null) {
              awaitSupported.current = true;
            }

            const avancou = (resp.versao ?? -1) > (cur ?? -1);

            if (resp.changed || avancou) {
              versaoRef.current = resp.versao ?? cur;
              await carregar();
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }

            continue;
          }

          await sleep(POLL_FALLBACK_MS);
          await carregar();
        }
      } catch (e) {
        if (!stop.current) console.error("useSaidasSuprimentos loop error:", e);
        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };
  }, [JSON.stringify(query)]);

  const refresh = async () => {
    while (fetching.current) await sleep(25);
    await carregar();
  };

  return { rows, rowMeta, loading, refresh, meta };
}
